// Day handling for the LinkedIn touches. Calendar dates arrive as "YYYY-MM-DD" strings and must never go through
// `new Date(string)` for display (that parses as UTC and can shift the day in other zones): they are formatted from their parts.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-11-04" -> "Wed 4 Nov 2026" (no timezone arithmetic). */
export function formatCalendarDate(value: string | null | undefined, withWeekday = true): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const weekday = WEEKDAYS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()];
  return `${withWeekday ? weekday + " " : ""}${d} ${MONTHS[mo - 1]} ${y}`;
}

/** A stored UTC timestamp shown in Asia/Karachi. */
export function formatKarachi(ts: string | null | undefined): string {
  if (!ts) return "";
  const iso = /Z|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Karachi", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(iso)) + " PKT";
}

/** Today's calendar date in Asia/Karachi ("YYYY-MM-DD"): the day the server uses for every day rule, whatever the browser's zone. */
export function karachiToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Karachi", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

export const KIND_LABELS: Record<string, string> = {
  connection_request: "Connection request",
  acceptance_message: "Message after acceptance",
  follow_up: "Follow-up",
};

/** "Fri 11:00 PM" for a stored UTC timestamp, in Asia/Karachi (12-hour, as the Logged badge shows times). */
export function formatKarachiClock(ts: string | null | undefined): string {
  if (!ts) return "";
  const iso = /Z|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`;
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Karachi", weekday: "short", hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(iso));
}

/** The Karachi calendar date ("YYYY-MM-DD") of a stored UTC timestamp. */
export function karachiDateOf(ts: string): string {
  const iso = /Z|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`;
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Karachi", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}

/** "today", "yesterday" or "3 days ago" for a calendar date ("YYYY-MM-DD", the Karachi day), or "" when it is in the future or unreadable. */
export function daysAgo(value: string | null | undefined, today: string = karachiToday()): string {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(today);
  if (!a || !b) return "";
  const n = Math.round((Date.UTC(+b[1], +b[2] - 1, +b[3]) - Date.UTC(+a[1], +a[2] - 1, +a[3])) / 86400000);
  if (n < 0) return "";
  return n === 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;
}
